import { useEffect, useRef, useState } from "react";
import { State } from "redux/store";
import { connect } from "react-redux";
import { StepTileProgressStatus } from "types";

import StepTile from "../StepTile";
import ChevronIcon from "assets/icons/ChevronIcon";
import IndexTypeIcon from "assets/icons/IndexTypeIcon";

import {
  getIsDatasetIndexing,
  getIsNewDatasetReady,
} from "redux/CreateDataset/selectors";
import { renderHeadIcon } from "../helpers";

import IndexTypeDropdown from "./IndexTypeDropdown";
import PulsingDropdownIcon from "../PulsingDropdownIcon";

import styles from "./style.module.scss";

const IndexTypeStep = ({
  datasetIndexing,
  isDatasetReady,
  indexType,
  setIndexType,
  indexTypeStatus,
  markIndexTypeComplete,
}: IndexTypeStepProps) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const editButtonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isActive = indexTypeStatus === StepTileProgressStatus.ACTIVE;
  const isComplete = indexTypeStatus === StepTileProgressStatus.COMPLETE;

  const stepStatus = indexTypeStatus;

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleOutsideClick = (event: MouseEvent) => {
    const editButton = editButtonRef.current;
    const dropdown = dropdownRef.current;

    const editButtonClicked =
      editButton && editButton.contains(event.target as Node);
    const dropdownClicked = dropdown && dropdown.contains(event.target as Node);

    if (showDropdown) {
      if (!editButtonClicked && !dropdownClicked) {
        setShowDropdown(false);
        markIndexTypeComplete();
      }
    }
  };

  const handleEditClick = () => {
    setShowDropdown(!showDropdown);
  };

  const handleIndexTypeSelection = (indexTypeId: string) => {
    setIndexType(indexTypeId);
    setShowDropdown(false);
    if (!isComplete) {
      markIndexTypeComplete();
    }
  };

  const renderActionIcon = () => {
    if (datasetIndexing || isDatasetReady) {
      return <noscript />;
    }

    if (isComplete) {
      return (
        <div
          ref={editButtonRef}
          className={styles.actionButton}
          onClick={handleEditClick}
        >
          EDIT
        </div>
      );
    }
    if (showDropdown) {
      return (
        <div
          ref={editButtonRef}
          onClick={handleEditClick}
          className={styles.openIcon}
        >
          <ChevronIcon direction="up" />
        </div>
      );
    } else if (isActive && !showDropdown) {
      return (
        <PulsingDropdownIcon
          showPulse={true}
          isDropdownOpen={showDropdown}
          toggleDropdown={toggleDropdown}
          containerRef={editButtonRef}
        />
      );
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
    //eslint-disable-next-line
  }, [showDropdown]);

  return (
    <div className={styles.indexTypeContainer}>
      <StepTile
        title={"Select Index Type"}
        status={stepStatus}
        headIcon={renderHeadIcon(<IndexTypeIcon />, stepStatus)}
        actionIcon={renderActionIcon()}
        handleOutsideClick={handleOutsideClick}
        isContentShown={showDropdown}
      />

      {showDropdown && (
        <IndexTypeDropdown
          dropdownRef={dropdownRef}
          selectedIndexType={indexType}
          handleSelection={handleIndexTypeSelection}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return {
    isDatasetReady: getIsNewDatasetReady(state),
    datasetIndexing: getIsDatasetIndexing(state),
  };
};

export default connect(mapStateToProps)(IndexTypeStep);

interface IndexTypeStepProps {
  datasetIndexing: boolean;
  isDatasetReady: boolean;
  indexType: string;
  setIndexType: (type: string) => void;
  indexTypeStatus: StepTileProgressStatus;
  markIndexTypeComplete: () => void;
}
